import React, { useCallback, useEffect, useRef, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useUserContext } from "../../context/UserProvider";
import { Form } from "react-bootstrap";
import { TruckIcon } from "../util/Icons";
import { onCreateUser, writeUserDetails } from "../../func/writeFunctions";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import Spinner from "../util/Spinner";
import { usePopUpContext } from "../../context/PopUpProvider";
import { useLocation } from "react-router-dom";
import AddressProfileFields from "../util/AddressProfileFields";
import MembershipJoin from "./MembershipJoin";
import {
  MEMBERSHIP_PRICING_DOC,
  normalizeMembershipPricing,
  pickMembershipPrice,
} from "../../utils/membershipPricing";
import {
  CancelMembershipModal,
  MemberDetails,
  MemberPerkCard,
  MemberQuickLinks,
  MemberScreen,
} from "./MembershipStatus";
import {
  getMembershipAccess,
  getMembershipDate,
  isCurrentMembershipSubscription,
} from "../../utils/membershipAccess";

const DEFAULT_FREE_MEMBERS_CHALLENGE = {
  name: "Slush & Sweat Challenge",
  url: "https://race.mychallengecanada.ca/register/march2026",
};
const DAILY_MEMBERSHIP_TEST_PRICE_ID = "price_1SpJSRKSwTC8CjLX8pG7LnFf";
const DAILY_MEMBERSHIP_TEST_PARAM = "testDailyMembership";
const STAGING_OR_LOCAL_ORIGINS = new Set([
  "https://mcc-staging-4525c.web.app",
  "http://localhost:3000",
]);

export default function MembershipPage() {
  const location = useLocation();
  const couponCode = new URLSearchParams(location.search).get("coupon")?.trim() || "";
  const { user, userDetails, membershipAccess } = useUserContext();
  const userId = user?.uid;
  const { showPopUp } = usePopUpContext()
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [showMembershipActions, setShowMembershipActions] = useState(false);
  const [priceList, setPriceList] = useState(null);
  const [membershipPricing, setMembershipPricing] = useState(() => normalizeMembershipPricing(null));
  const [loading, setLoading] = useState(false);
  const [membershipActionLoading, setMembershipActionLoading] = useState(false);
  const [membershipActionMessage, setMembershipActionMessage] = useState(null);
  const [membershipDetails, setMembershipDetails] = useState(undefined)
  const [freeMembersChallenge, setFreeMembersChallenge] = useState(DEFAULT_FREE_MEMBERS_CHALLENGE);
  const selectedPrice = useRef(null);
  const selectedCheckoutMode = useRef("default");
  const testDailyCheckoutStarted = useRef(false);
  const form = useRef(null);
  // console.log(membershipDetails);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    fetchMembershipPageData();

    async function fetchMembershipPageData() {
      setMembershipDetails(undefined);

      try {
        const [subscriptionsSnap, challengeData] = await Promise.all([
          getDocs(collection(db, "stripe_customers", userId, "subscriptions")),
          fetchFreeMembersChallenge(),
        ]);
        const subscriptions = subscriptionsSnap.docs.map((subscriptionDoc) => ({
          ...subscriptionDoc.data(),
          id: subscriptionDoc.id,
        }));
        const nextMembershipDetails = getDisplayMembershipDetails(subscriptions);

        if (cancelled) return;
        setMembershipDetails(nextMembershipDetails);
        setFreeMembersChallenge(challengeData);

        if (!isCurrentMember(nextMembershipDetails)) {
          await fetchMembershipPrices();
        }
      } catch (error) {
        console.error("Unable to load membership details", error);
        if (!cancelled) {
          setMembershipDetails(null);
        }
        await fetchMembershipPrices();
      }
    }

    async function fetchMembershipPrices() {
      setLoading(true);
      const products = [];
      const newPriceList = [];
      // console.log("fetching membership products");
      try {
        const [membershipProductDocs, pricing] = await Promise.all([
          getDocs(
            query(collection(db, "stripe_products"), where("role", "==", "member")),
          ),
          fetchMembershipPricing(),
        ]);
        for (let doc of membershipProductDocs.docs) {
          products.push({ ...doc.data(), id: doc.id });
        }
        for (let t of products) {
          let id = t.id;
          const priceDocs = await getDocs(
            collection(db, "stripe_products", id, "prices"),
          );
          // Admins choose the price for new members in options/membershipPricing.
          const price = pickMembershipPrice(
            priceDocs.docs.map((priceDoc) => ({ ...priceDoc.data(), id: priceDoc.id })),
            pricing.activePriceId,
          );
          if (price) {
            newPriceList.push({ ...t, price });
          }
        }
        if (!cancelled) {
          setMembershipPricing(pricing);
          setPriceList([...newPriceList]);
        }
      } catch (error) {
        console.error("Unable to load membership prices", error);
        if (!cancelled) {
          setPriceList([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
      // console.log(newPriceList);
    }

    async function fetchMembershipPricing() {
      try {
        const pricingSnap = await getDoc(doc(db, ...MEMBERSHIP_PRICING_DOC));
        return normalizeMembershipPricing(pricingSnap?.exists?.() ? pricingSnap.data() : null);
      } catch (error) {
        console.error("Unable to load membership pricing", error);
        return normalizeMembershipPricing(null);
      }
    }

    async function fetchFreeMembersChallenge() {
      try {
        const optionSnap = await getDoc(doc(db, "options", "freeMembersChallenge"));
        if (optionSnap.exists()) {
          const optionData = optionSnap.data();
          return {
            name: optionData?.name || DEFAULT_FREE_MEMBERS_CHALLENGE.name,
            url: optionData?.url || DEFAULT_FREE_MEMBERS_CHALLENGE.url,
          };
        }
      } catch (error) {
        console.error("Unable to load free members challenge", error);
      }

      return DEFAULT_FREE_MEMBERS_CHALLENGE;
    }
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const createCheckout = useCallback(async (e, priceOverride = null, options = {}) => {
    if (!userId) return;
    if (loading) return;
    const checkoutPriceId = priceOverride || selectedPrice.current;
    if (!checkoutPriceId) return;
    setLoading("finalStep");
    e && e.preventDefault();
    console.log("Creating checkout for membership...");

    if (options.mode !== "restart" && !couponCode) {
      const checkoutRef = await addDoc(
        collection(db, "stripe_customers", userId, "checkout_sessions"),
        {
          automatic_tax: true,
          line_items: [
            {
              price: checkoutPriceId,
              quantity: 1,
            },
            {
              price_data: {
                currency: "cad",
                unit_amount: 690,
                tax_behavior: "exclusive",
                product_data: {
                  name: "One-Time Setup Fee",
                  description:
                    "This applies only to your initial membership payment. Subsequent payments will not include it.",
                },
              },
              quantity: 1,
            },
          ],
          success_url: window.location.origin + "/membership",
          cancel_url: window.location.origin + "/membership",
        },
      );

      await new Promise((resolve) => {
        const onCheckout = onSnapshot(checkoutRef, (snap) => {
          const { error, url } = snap.data();
          if (error) {
            onCheckout();
            alert(`An error occured: ${error.message}`);
            resolve();
            setLoading(false);
          }
          if (url) {
            onCheckout();
            window.open(url, "_self");
            resolve();
          }
        });
      });
      console.log("done");
      return;
    }

    try {
      const functionRef = httpsCallable(
        getFunctions(),
        "createMembershipCheckout",
      );
      const { data } = await functionRef({
        priceId: checkoutPriceId,
        successUrl: window.location.origin + "/membership",
        cancelUrl: window.location.origin + "/membership" +
          (couponCode ? `?coupon=${encodeURIComponent(couponCode)}` : ""),
        ...(couponCode ? { coupon: couponCode } : {}),
        checkoutMode: options.mode === "restart" ? "restart" : "initial",
      });
      if (!data?.url) {
        throw new Error("Checkout URL was not returned.");
      }
      window.open(data.url, "_self");
      console.log("done");
    } catch (error) {
      console.error("Unable to create membership checkout", error);
      alert(`An error occurred: ${error.message || "Unable to create checkout."}`);
      setLoading(false);
    }
  }, [couponCode, loading, userId]);

  const beginCheckoutForPrice = useCallback((priceId, options = {}) => {
    selectedPrice.current = priceId;
    selectedCheckoutMode.current = options.mode || "default";
    if (!userDetails || Object.entries(userDetails).length === 0) {
      setShowDetailsForm(true);
      return;
    }

    createCheckout(null, priceId, options);
  }, [createCheckout, userDetails]);

  useEffect(() => {
    if (
      testDailyCheckoutStarted.current ||
      !userId ||
      loading ||
      membershipDetails === undefined ||
      !isDailyMembershipTestRequest()
    ) {
      return;
    }

    if (isCurrentMember(membershipDetails)) return;

    testDailyCheckoutStarted.current = true;
    beginCheckoutForPrice(DAILY_MEMBERSHIP_TEST_PRICE_ID);
  }, [beginCheckoutForPrice, loading, membershipDetails, userId]);

  async function createUserAndChekout(e) {
    if (loading) return;
    setLoading(true);
    e.preventDefault();
    if (!form.current) return;
    const formDetails = Object.fromEntries(
      new FormData(form.current).entries(),
    );
    console.log(formDetails);

    await onCreateUser(user);
    await writeUserDetails(user, formDetails);

    setLoading(false);
    createCheckout(null, null, { mode: selectedCheckoutMode.current });
  }

  async function openPortal(e) {
    if (loading) return;
    setLoading(true);
    e && e.preventDefault();
    console.log("Manage your subscription with this portal");

    const functionRef = httpsCallable(
      getFunctions(),
      "ext-firestore-stripe-payments-createPortalLink",
    );
    const { data } = await functionRef({
      returnUrl: window.location.origin + "/membership",
      // locale: "auto", // Optional, defaults to "auto"
      // configuration: "bpc_1JSEAKHYgolSBA358VNoc2Hs", // Optional ID of a portal configuration: https://stripe.com/docs/api/customer_portal/configuration
    });
    // window.location.assign(data.url);
    window.open(data.url, "_self");

    console.log("done");
    setLoading(false);
  }

  async function keepMembershipActive(e) {
    if (membershipActionLoading || !membershipDetails?.id) return;
    e && e.preventDefault();
    setMembershipActionLoading(true);
    setMembershipActionMessage(null);

    try {
      const functionRef = httpsCallable(
        getFunctions(),
        "keepMembershipActive",
      );
      const { data } = await functionRef({
        subscriptionId: membershipDetails.id,
      });

      setMembershipDetails((currentDetails) => {
        if (!currentDetails) return currentDetails;
        return {
          ...currentDetails,
          status: data?.status || currentDetails.status,
          cancel_at_period_end: false,
          cancel_at: null,
        };
      });
      setShowMembershipActions(false);
    } catch (error) {
      console.error("Unable to keep membership active", error);
      setMembershipActionMessage(
        "We couldn't update your membership. Please try again.",
      );
    } finally {
      setMembershipActionLoading(false);
    }
  }

  function showCancelMembershipPopUp(periodEnd) {
    return showPopUp(
      <CancelMembershipModal
        periodEnd={periodEnd}
        onConfirmCancel={openPortal}
      />
    );
  }

  if (loading) {
    if (loading === "finalStep") {
      return (
        <div
          className="flex flex-col items-center text-center gap-1"
          role="status"
          aria-live="polite"
        >
          <div className="w-40 h-40" aria-hidden="true">
            <DotLottieReact
              src={`${process.env.PUBLIC_URL}/animations/walking-shoes.lottie`}
              autoplay
              loop
            />
          </div>
          <h2>Personalizing your experience</h2>
        </div>
      );
    }

    return <div className="flex flex-col text-center gap-1">
      <Spinner />
    </div>;
  }

  if (membershipDetails === undefined) {
    return <div className="flex flex-col text-center gap-1">
      <Spinner />
    </div>
  }

  const currentMembershipDetails = membershipDetails || membershipAccess?.subscription || null;

  if (membershipAccess?.state === "past_due_grace" || membershipAccess?.state === "past_due_expired") {
    const periodStart = getMembershipDate(currentMembershipDetails?.current_period_start);
    const periodEnd = getMembershipDate(currentMembershipDetails?.current_period_end);
    const accessEndsAt = getMembershipDate(membershipAccess?.accessEndsAt);
    const plan = getMembershipPlan(currentMembershipDetails);
    const hasTemporaryAccess = membershipAccess.state === "past_due_grace";

    return (
      <MemberScreen
        tone="warn"
        badge="Payment needs attention"
        title="Update your payment method"
        intro={hasTemporaryAccess
          ? `We couldn’t process your latest payment. You still have temporary access to member content, but race perks are paused. To keep your membership active and restore your perks, please update your payment info${accessEndsAt ? ` by ${accessEndsAt.toLocaleDateString()}` : ""}.`
          : "We couldn’t process your latest membership payment, and your temporary member content access has ended. Update your payment method to reactivate your membership."}
      >
        <MemberDetails
          chips={[
            { tone: "warn", label: hasTemporaryAccess ? "Temporary Access" : "Access Paused" },
            plan && { tone: "plan", label: formatMembershipPlan(plan) },
          ]}
          dates={[
            { label: "Period Start", value: periodStart },
            { label: "Billing Period End", value: periodEnd },
            { label: hasTemporaryAccess ? "Access Until" : "Access Ended", value: accessEndsAt },
          ]}
        />
        <div className="ms-actions">
          <button type="button" onClick={openPortal} className="ms-btn">
            Update payment method
          </button>
        </div>
      </MemberScreen>
    );
  }

  if (membershipAccess?.state === "trialing") {
    const periodStart = getMembershipDate(currentMembershipDetails?.current_period_start);
    const periodEnd = getMembershipDate(
      currentMembershipDetails?.trial_end ||
      currentMembershipDetails?.current_period_end
    );
    const plan = getMembershipPlan(currentMembershipDetails);

    return (
      <MemberScreen
        badge="Membership started"
        title="Welcome to Momentum"
        intro="Your member content is active. Your free member challenge credit unlocks after your first membership payment is complete."
        links={<MemberQuickLinks />}
      >
        <MemberDetails
          chips={[
            { tone: "ok", label: "Content Access Active" },
            plan && { tone: "plan", label: formatMembershipPlan(plan) },
          ]}
          dates={[
            { label: "Period Start", value: periodStart },
            { label: "First Payment", value: periodEnd, fallback: "Pending" },
          ]}
        />
        <div className="ms-actions">
          <button type="button" onClick={openPortal} className="ms-btn-ghost">
            Manage billing
          </button>
        </div>
      </MemberScreen>
    );
  }

  if (isCancelledMember(membershipDetails)) {
    const periodStart = getMembershipDate(membershipDetails?.current_period_start);
    const periodEnd = getMembershipDate(
      membershipDetails?.ended_at ||
      membershipDetails?.current_period_end ||
      membershipDetails?.cancel_at
    );
    const cancelledOn = getMembershipDate(
      membershipDetails?.canceled_at ||
      membershipDetails?.cancel_at
    );
    const plan = getMembershipPlan(membershipDetails);
    const isEndingAtPeriodEnd = isCurrentMember(membershipDetails);
    const statusLabel = isEndingAtPeriodEnd && periodEnd
      ? `Ends on ${periodEnd.toLocaleDateString()}`
      : "Cancelled";
    const endDateLabel = isEndingAtPeriodEnd ? "Membership Ends" : "Membership Ended";
    const canKeepMembershipActive = Boolean(membershipDetails?.cancel_at_period_end);

    return (
      <MemberScreen
        tone="warn"
        badge={isEndingAtPeriodEnd ? "Membership ending" : "Membership cancelled"}
        title={isEndingAtPeriodEnd ? "Your membership is ending" : "Your membership has ended"}
        intro={isEndingAtPeriodEnd
          ? "You still have member access until the end of your billing period."
          : "You can restart your Momentum membership whenever you're ready."}
        perk={isEndingAtPeriodEnd && (
          <MemberPerkCard freeMembersChallenge={freeMembersChallenge} />
        )}
      >
        <MemberDetails
          chips={[
            { tone: "warn", label: statusLabel },
            plan && { tone: "plan", label: formatMembershipPlan(plan) },
          ]}
          dates={[
            { label: "Period Start", value: periodStart },
            { label: "Cancelled On", value: cancelledOn },
            { label: endDateLabel, value: periodEnd },
          ]}
        />
        {isEndingAtPeriodEnd ? (
          <>
            <div className="ms-actions">
              {canKeepMembershipActive && (
                <button
                  type="button"
                  onClick={keepMembershipActive}
                  disabled={membershipActionLoading}
                  className="ms-btn"
                >
                  {membershipActionLoading ? "Keeping membership..." : "Keep my membership"}
                </button>
              )}
              <button type="button" onClick={openPortal} className="ms-btn-ghost">
                Manage billing
              </button>
            </div>
            {membershipActionMessage && (
              <p className="ms-error">{membershipActionMessage}</p>
            )}
          </>
        ) : (
          <div className="ms-actions">
            {priceList && priceList.map((product) => {
              const price = product.price;
              const priceId = price.id;
              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => beginCheckoutForPrice(priceId, { mode: "restart" })}
                  className="ms-btn"
                >
                  Restart Membership
                </button>
              );
            })}
          </div>
        )}
      </MemberScreen>
    );
  }

  if (isCurrentMember(membershipDetails)) {

    const periodStart = getMembershipDate(membershipDetails?.current_period_start)
    const periodEnd = getMembershipDate(membershipDetails?.current_period_end)
    const plan = getMembershipPlan(membershipDetails)
    const isCancelingAtPeriodEnd = Boolean(
      membershipDetails?.cancel_at_period_end ||
      membershipDetails?.cancel_at
    );
    const statusLabel = isCancelingAtPeriodEnd && periodEnd
      ? `Cancels on ${periodEnd.toLocaleDateString()}`
      : membershipDetails?.status || "Active";
    const periodEndLabel = isCancelingAtPeriodEnd ? "Membership Ends" : "Next Billing Date";
    return (
      <MemberScreen
        badge="Active member"
        title="Welcome to Momentum"
        intro={'Keep up the momentum — you\'re on your way to "Finished."'}
        perk={<MemberPerkCard freeMembersChallenge={freeMembersChallenge} />}
        links={<MemberQuickLinks />}
      >
        <MemberDetails
          chips={[
            { tone: isCancelingAtPeriodEnd ? "warn" : "ok", label: statusLabel },
            { tone: "plan", label: plan ? formatMembershipPlan(plan) : "Plan unavailable" },
          ]}
          dates={[
            { label: "Period Start", value: periodStart },
            { label: periodEndLabel, value: periodEnd },
          ]}
        />
        <div className="ms-actions">
          <button
            type="button"
            onClick={() => setShowMembershipActions((prev) => !prev)}
            className="ms-link"
          >
            Manage my membership
          </button>
        </div>
        {showMembershipActions && (
          <div className="ms-manage">
            <button type="button" onClick={openPortal} className="ms-btn-ghost">
              Manage my billing and payment information
            </button>
            <button
              type="button"
              onClick={() => showCancelMembershipPopUp(periodEnd)}
              className="ms-btn-ghost"
            >
              Cancel my membership
            </button>
          </div>
        )}
      </MemberScreen>
    );
  }

  return (
    <div className="mj-page">
      {!showDetailsForm ? (
        <MembershipJoin
          priceList={priceList}
          pricing={membershipPricing}
          couponCode={couponCode}
          onCheckout={beginCheckoutForPrice}
        />
      ) : (
        /* Details Form */
        <div className="mj-form-card">
          <Form ref={form} onSubmit={createUserAndChekout}>
            <div className="mb-6 text-center">
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#150f33' }}>
                Almost there!
              </h3>
              <div className="flex items-center justify-center gap-2 mb-2" style={{ color: '#7b44b5' }}>
                <TruckIcon size={20} />
                <p className="text-sm">Your SWAG kit will be shipped to this address. (when applicable)</p>
              </div>
            </div>

            <div className="space-y-4">
              <AddressProfileFields initialDetails={userDetails} />

              {/* Submit Button */}
              <button type="submit" className="mj-btn w-full justify-center mt-4">
                Become a Member
              </button>

              <button
                type="button"
                onClick={() => setShowDetailsForm(false)}
                className="w-full py-2 text-sm transition-all hover:opacity-80"
                style={{ color: '#5d5878' }}
              >
                ← Go Back
              </button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}

function StyledButton({ children, onClick, type = "button", className = "" }) {
  return (
    <button
      type={type}
      className={"bg-blue-500 text-white rounded-xl p-2 " + className}
      onClick={(e) => onClick(e)}
    >
      {children}
    </button>
  );
}

function isDailyMembershipTestRequest() {
  if (typeof window === "undefined") return false;
  if (!STAGING_OR_LOCAL_ORIGINS.has(window.location.origin)) return false;
  return new URLSearchParams(window.location.search).get(DAILY_MEMBERSHIP_TEST_PARAM) === "1";
}

function getDisplayMembershipDetails(subscriptions) {
  if (!Array.isArray(subscriptions) || subscriptions.length === 0) return null;

  // Keep the membership page aligned with the access calculation used by the
  // rest of the app and the backend. In particular, an active membership that
  // is scheduled to cancel must take precedence over historical subscriptions.
  return getMembershipAccess(subscriptions).subscription;
}

function isCurrentMember(subscription) {
  return isCurrentMembershipSubscription(subscription);
}

function hasScheduledCancellation(subscription) {
  return Boolean(
    isCurrentMember(subscription) &&
    (subscription?.cancel_at_period_end || subscription?.cancel_at)
  );
}

function isCancelledMember(subscription) {
  return Boolean(
    subscription &&
    (["canceled", "cancelled"].includes(subscription.status) || hasScheduledCancellation(subscription))
  );
}

function getMembershipPlan(membershipDetails) {
  return (
    membershipDetails?.items?.[0]?.plan ||
    membershipDetails?.items?.[0]?.price ||
    membershipDetails?.plan ||
    null
  );
}

function formatMembershipPlan(plan) {
  const amount = typeof plan?.amount === "number" ? plan.amount : plan?.unit_amount;
  const price = typeof amount === "number" ? `$${formatCurrencyAmount(amount)}` : "Price unavailable";
  const currency = plan?.currency ? plan.currency.toUpperCase() : "";
  const intervalCount = plan?.interval_count || plan?.recurring?.interval_count || 1;
  const interval = plan?.interval || plan?.recurring?.interval || "";
  const intervalLabel = interval
    ? `${intervalCount > 1 ? `${intervalCount} ` : ""}${interval}`
    : "";

  return `${price}${currency ? ` ${currency}` : ""}${intervalLabel ? ` / ${intervalLabel}` : ""}`;
}

function formatCurrencyAmount(amount) {
  const dollars = amount / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

